import { Loader, LoaderProps } from '@mantine/core';

// Tamaños predefinidos para consistencia en la aplicación
const LOADER_SIZES = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
  xl: 40,
} as const;

export type AfaLoaderSize = keyof typeof LOADER_SIZES | number | string;

export interface AfaLoaderProps extends Omit<LoaderProps, 'size'> {
  /**
   * Tamaño del loader
   * - xs: 16px - Para espacios muy pequeños
   * - sm: 20px - Para botones pequeños
   * - md: 24px - Tamaño estándar (defecto)
   * - lg: 32px - Para áreas principales
   * - xl: 40px - Para pantallas de carga
   * - number: Tamaño en píxeles
   * - string: Cualquier valor CSS válido
   */
  size?: AfaLoaderSize;
}

/**
 * Componente AfaLoader - Wrapper del Mantine Loader con configuración AFA
 * 
 * Características:
 * - Color brand por defecto (#00476B - Primario/themePrimary)
 * - Tamaños predefinidos xs, sm, md, lg, xl
 * - Tamaño md (24px) por defecto
 * - Soporte para tamaños personalizados (number o string CSS)
 * 
 * Ejemplos de uso:
 * ```tsx
 * // Básico
 * <AfaLoader />
 * 
 * // Tamaños predefinidos
 * <AfaLoader size="xs" />
 * <AfaLoader size="lg" />
 * 
 * // Tamaño personalizado
 * <AfaLoader size={28} />
 * <AfaLoader size="2rem" />
 * 
 * // Diferentes tipos
 * <AfaLoader type="dots" />
 * <AfaLoader type="bars" />
 * 
 * // Color personalizado
 * <AfaLoader color="red" />
 * ```
 */
export function AfaLoader({ 
  color = 'brand.5', 
  size = 'lg', 
  ...props 
}: AfaLoaderProps) {
  // Resolver el tamaño: si es una key de LOADER_SIZES, usar el valor; si no, usar tal como viene
  const resolvedSize = typeof size === 'string' && size in LOADER_SIZES 
    ? LOADER_SIZES[size as keyof typeof LOADER_SIZES]
    : size;

  return (
    <Loader
      color={color}
      size={resolvedSize}
      {...props}
    />
  );
}